// @ts-nocheck - layout.tsx uses wac-web dependencies (@/) which are outside rootDir
import { useEffect, useState } from "react";

import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import {
    WacAnalyticsClientProvider,
    WacAnalyticsListener
} from "@/analytics/wac-analytics";
import { CrossFlowProvider } from "@/components/cross-flow";
import { SitesContextProvider } from "@/components/sites-provider";
import { UserContextProvider } from "@/components/user-context";
import {
    AvailableProductsResponse,
    BxpWebStargateClientProvider,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "@/libraries/bxp-web-stargate-client/src";
import { getStargateBaseUrl } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { GrowthUnifiedProfile } from "@/libraries/growth-unified-profile-client";
import { trackRouteUFOV1PageLoad } from "@/ufo-v1";
import { GridOverlayWrapper } from "@/utils/grid-overlay";
import { useAnchorLocalStorage } from "@/utils/local-storage-hook";
import { LocationProvider } from "@/utils/location-provider";
import { LoggedInUserProvider } from "@/utils/logged-in-user-provider";
import { OneTrustProvider } from "@/utils/onetrust-provider";
import { SmartReCaptcha } from "@/utils/smart-recaptcha";
import { SSRLoginStateProvider } from "@/utils/ssr-login-state-provider";
import { UserLocale } from "@/utils/useGeoCurrency";

import { parseRoute } from "./trello-portal-routes";

// Content API types (local definitions, no external dependencies)
interface PageModel {
    id: string;
    type: string;
    locale: string;
    content: {
        title: string;
        metaDescription?: string;
        noIndexTag?: boolean;
        noFollowTag?: boolean;
        route: {
            id: string;
            type: string;
            locale: string;
            content: {
                path: string;
            };
        };
        [key: string]: unknown;
    };
}

interface RouteParams {
    spaceId?: string;
    environmentId?: string;
    language?: string;
    path?: string;
}

/**
 * Simplified Layout for Trello Portal
 * Only includes properties actually used by trello components
 */
export type LayoutProps = {
    analyticsWebClient: AnalyticsWebClient | null;
    location: Location;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    hasCloudSessionTokenCookie?: boolean;
    children: React.ComponentType<PageProps>;
    userAgent?: string;
    pageModel?: PageModel | null;
    contentRendererParams?: RouteParams;
    isNotFound?: boolean;
};

/**
 * Simplified PageProps for Trello Portal
 * Only includes properties actually used by trello components
 */
export type PageProps = {
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    pageModel?: PageModel | null;
    contentRendererParams?: RouteParams;
    isNotFound?: boolean;
};

// UFOSegment wrapper component that's safe for SSR
// UFOSegment uses @atlaskit/tokens which tries to access DOM during render
// We dynamically import it only on the client side to avoid SSR issues
const UFOWrapper = ({ children }: { children: React.ReactNode }) => {
    const [UFOSegmentComponent, setUFOSegmentComponent] =
        useState<React.ComponentType<{
            name: string;
            children: React.ReactNode;
        }> | null>(null);

    useEffect(() => {
        // Only import and set UFOSegment on the client side
        import("@atlaskit/react-ufo/segment").then((mod) => {
            setUFOSegmentComponent(() => mod.default);
        });
    }, []);

    // During SSR or before the component loads, just render children
    if (!UFOSegmentComponent) {
        return <>{children}</>;
    }

    // On client side, wrap with UFOSegment
    return <UFOSegmentComponent name="wac">{children}</UFOSegmentComponent>;
};

export const Layout = (props: LayoutProps) => {
    const { locale, path } = parseRoute(location);
    useAnchorLocalStorage();

    // Track if component has mounted (client-side only)
    const [isMounted, setIsMounted] = useState(false);
    const [InitUFOComponent, setInitUFOComponent] =
        useState<React.ComponentType | null>(null);

    useEffect(() => {
        setIsMounted(true);
        // Dynamically import InitUFOV1 only on client side to avoid SSR issues with @atlaskit/react-ufo
        import("@/analytics/ufo/initUFO").then((mod) => {
            setInitUFOComponent(() => mod.InitUFOV1);
        });
    }, []);

    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        trackRouteUFOV1PageLoad(path);
    }

    useEffect(() => {
        // Send operational event when static fallback is detected
        if (window.IS_STATIC && props.analyticsWebClient) {
            props.analyticsWebClient.sendOperationalEvent({
                action: "detected",
                actionSubject: "staticWACFallback",
                source: "layout",
                attributes: {
                    route: window.location.pathname,
                    userAgent: props.userAgent,
                    anonymousId: props.analyticsWebClient.getAnonymousId()
                }
            });
        }
    }, [props.analyticsWebClient, props.userAgent]);

    return (
        <OneTrustProvider>
            {process.env["NODE_ENV"] !== "production" && <GridOverlayWrapper />}
            <WacAnalyticsClientProvider
                analyticsWebClient={props.analyticsWebClient}
                location={props.location}
            >
                {InitUFOComponent && <InitUFOComponent />}
                <LocationProvider location={props.location}>
                    <UFOWrapper>
                        <SmartReCaptcha>
                            <BxpWebStargateClientProvider
                                baseUrl={getStargateBaseUrl()}
                            >
                                <SSRLoginStateProvider
                                    hasCloudSessionTokenCookie={
                                        props.hasCloudSessionTokenCookie ??
                                        false
                                    }
                                    userAgent={props.userAgent}
                                >
                                    <LoggedInUserProvider>
                                        <WacAnalyticsListener>
                                            <CrossFlowProvider locale={locale}>
                                                <SitesContextProvider>
                                                    <UserContextProvider
                                                        location={
                                                            props.location.href
                                                        }
                                                        meResponse={
                                                            props.userProfile
                                                        }
                                                        meRequest={
                                                            props.userProfileStatus
                                                        }
                                                        userProfileResponse={
                                                            props.userContext
                                                        }
                                                        userProfileRequest={
                                                            props.userContextStatus
                                                        }
                                                        loggedOutContext={
                                                            props.loggedOutContext
                                                        }
                                                        loggedOutContextStatus={
                                                            props.loggedOutContextStatus
                                                        }
                                                        loggedInContext={
                                                            props.loggedInContext
                                                        }
                                                        loggedInContextStatus={
                                                            props.loggedInContextStatus
                                                        }
                                                    >
                                                        <props.children
                                                            {...props}
                                                            pageModel={
                                                                props.pageModel
                                                            }
                                                            contentRendererParams={
                                                                props.contentRendererParams
                                                            }
                                                        />
                                                    </UserContextProvider>
                                                </SitesContextProvider>
                                            </CrossFlowProvider>
                                        </WacAnalyticsListener>
                                    </LoggedInUserProvider>
                                </SSRLoginStateProvider>
                            </BxpWebStargateClientProvider>
                        </SmartReCaptcha>
                    </UFOWrapper>
                </LocationProvider>
            </WacAnalyticsClientProvider>
        </OneTrustProvider>
    );
};
